import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCandidatesForDecisionCenterThunk } from '../../store/thunks/InterviewThunk';
import {
  ArrowDownZA,
  ArrowUpAz,
  ArrowUpDown,
  ChevronsLeft,
  ChevronsRight,
  EllipsisVertical,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateCandidateStatusService } from '../../Services/interviewManagement.service';
import ConfirmationDialog from './ConfirmationDialog';
import Pagination from './Pagination';
import {
  addCandidateToCompletedSection,
  sortDecisionCenterByCol,
  toggleAllDecisionCenterCandidatesStatus,
  toggleDecisionCenterCandidatesStatus,
  updateCandidateStatus,
} from '../../store/slices/InterviewManagementSlice';
import { toast } from 'react-toastify';
import {
  addcandidateToSelectedList,
  removeCandidateFromSelectedList,
  removeSelectedCandidates,
  updateSelectAllCandidateReducer,
} from '../../store/slices/candidateManagementSlice';
import Loader from '../Loader';

function DecisionCenter() {
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [showReasonModel, setShowReasonModel] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const menuRef = useRef(null);
  const location = useLocation();
  const selectAllClicked = useSelector(
    (state) => state.candidateManagementSlice.selectAllClicked,
  );
  const activeSubTab = useSelector(
    (state) => state.candidateManagementSlice.activeSubTab,
  );
  const activeTab = useSelector(
    (state) => state.candidateManagementSlice.activeTab,
  );
  const currentCols = useSelector(
    (state) => state.filterSlice.columnVisibility?.[activeTab]?.[activeSubTab],
  );
  const decisionCandidatesLoading = useSelector(
    (state) => state.interviewManagementSlice.decisionCandidatesLoading,
  );

  // ADD these two selectors near the top with other selectors:
  const acceptedCandidates = useSelector(
    (state) => state.interviewManagementSlice.acceptedCandidates,
  );
  const rejectedCandidates = useSelector(
    (state) => state.interviewManagementSlice.rejectedCandidates,
  );

  const visibleColumns = useMemo(() => {
    if (!currentCols || currentCols.length === 0) return {};
    return currentCols.reduce((acc, col) => {
      const key = Object.keys(col)[0];
      acc[key] = col[key];
      return acc;
    }, {});
  }, [currentCols]);
  const currentScoreRange = useSelector(
    (state) => state.filterSlice.currentScoreRange,
  );
  const sortConfig = useSelector(
    (state) => state.interviewManagementSlice.sortConfig,
  );

  const portal = location.pathname.startsWith('/hr') ? 'hr' : 'org-admin';

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux Filtering Work=====================
  const dateRange = useSelector((state) => state.filterSlice.dateRange);
  const currentJobRole = useSelector(
    (state) => state.filterSlice.currentJobRole,
  );
  const currentOwner = useSelector((state) => state.filterSlice.currentOwner);
  const searchQuery = useSelector((state) => state.filterSlice.searchQuery);
  const filterByDateRange = (candidates) => {
    if (!dateRange || dateRange.length !== 2) return candidates;

    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return candidates;

    const startTime = new Date(startDate).setHours(0, 0, 0, 0);
    const endTime = new Date(endDate).setHours(23, 59, 59, 999);

    return candidates.filter((c) => {
      if (!c.date_of_interview) return false;

      const candidateTime = Date.parse(c.date_of_interview);
      if (Number.isNaN(candidateTime)) return false;
      console.log({ candidateTime, startTime, endTime });
      return candidateTime >= startTime && candidateTime <= endTime;
    });
  };

  const handleSort = (key) => {
    const currentKey = sortConfig.key;
    const currentDir = sortConfig.direction;

    let newDirection;
    if (currentKey !== key) newDirection = 'asc';
    else if (currentDir === 'asc') newDirection = 'desc';
    else if (currentDir === 'desc') newDirection = null;
    else newDirection = 'asc';

    dispatch(sortDecisionCenterByCol({ key, direction: newDirection }));
  };

  //Filter By Job Role
  const filterByJobRole = (candidates) => {
    console.log({ currentJobRole });
    if (!currentJobRole || currentJobRole === '' || currentJobRole === 'All') {
      return candidates;
    }

    const datatoReturn = candidates.filter(
      (c) =>
        c.job_role && c.job_role.toLowerCase() === currentJobRole.toLowerCase(),
    );
    console.log({ datatoReturn });
    return datatoReturn;
  };

  const filterByName = (candidate) => {
    if (searchQuery === '') {
      return candidate;
    }
    return candidate.filter((candidate) =>
      candidate.candidate_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()),
    );
  };

  const filterByCurrentOwner = (candidates) => {
    if (currentOwner === '' || currentOwner === 'All') {
      return candidates;
    }
    return candidates.filter((c) => {
      if (!c.hr_owner) return false;
      return c.hr_owner === currentOwner;
    });
  };

  const filterByScoreRange = (candidates) => {
    const { min, max } = currentScoreRange || {};
    if (min === undefined || max === undefined) return candidates;
    if (min === 0 && max === 100) return candidates; // default range, skip

    return candidates.filter((c) => {
      const score = parseFloat(c.interview_score) || 0;
      return score >= min && score <= max;
    });
  };

  const filterByActiveJobRole = (candidates) => {
    if (!currentJobRole || currentJobRole === 'All') {
      return candidates;
    }
    return candidates.filter((c) => {
      if (!c?.job_role) return false;

      return c?.job_role?.toLowerCase() === currentJobRole.toLowerCase();
    });
  };

  const candidates = useSelector((state) => {
    const {
      acceptedCandidates,
      rejectedCandidates,
      mergedDecisionCenterCandidates,
      sortConfig,
    } = state.interviewManagementSlice;
    const activeSubTab = state.candidateManagementSlice.activeSubTab;

    const baseList =
      activeSubTab === 'ACCEPTED'
        ? acceptedCandidates
        : activeSubTab === 'REJECTED'
          ? rejectedCandidates
          : sortConfig?.key &&
              sortConfig?.direction &&
              mergedDecisionCenterCandidates.length > 0
            ? mergedDecisionCenterCandidates // ← globally sorted for ALL tab
            : [...acceptedCandidates, ...rejectedCandidates];

    return filterByScoreRange(
      filterByJobRole(
        filterByName(
          filterByCurrentOwner(
            filterByActiveJobRole(filterByDateRange(baseList)),
          ),
        ),
      ),
    );
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSubTab, searchQuery, dateRange, currentJobRole, currentOwner]);

  const totalCount = candidates.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCandidates = candidates.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // =========================================
  // Then replace the useEffect:
  useEffect(() => {
    if (activeSubTab === 'ALL') {
      if (acceptedCandidates.length === 0)
        dispatch(getCandidatesForDecisionCenterThunk('SELECTED'));
      if (rejectedCandidates.length === 0)
        dispatch(getCandidatesForDecisionCenterThunk('REJECTED'));
      return;
    }
    if (activeSubTab === 'ACCEPTED' && acceptedCandidates.length === 0) {
      dispatch(getCandidatesForDecisionCenterThunk('SELECTED'));
    }
    if (activeSubTab === 'REJECTED' && rejectedCandidates.length === 0) {
      dispatch(getCandidatesForDecisionCenterThunk('REJECTED'));
    }
  }, [activeSubTab]);

  const onViewCandidate = (interview_id) => {
    dispatch(removeSelectedCandidates());
    const basePath = portal === 'hr' ? '/hr' : '/org-admin';
    navigate(`${basePath}/evaluationpage/${interview_id}`, {
      state: {
        interview_id,
      },
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);
  useEffect(() => {
    dispatch(
      toggleAllDecisionCenterCandidatesStatus({ activeSubTab, value: false }),
    );
  }, [activeSubTab]);

  const handleupdateCandidateStatus = async (
    candidate,
    status,
    reason = '',
  ) => {
    console.log({ candidate });
    const toastId = toast.loading(
      status === 'REVERT'
        ? 'Reverting candidate...'
        : 'Updating candidate status...',
    );
    const response = await updateCandidateStatusService({
      candidate_email: candidate?.candidate_email || '',
      candidate_name: candidate?.candidate_name || '',
      status,
      reason,
      interview_id: candidate?.candidate_id,
      hr_owner: candidate?.hr_owner || '',
      date_of_interview: candidate?.date_of_interview || '',
      interview_score:
        candidate?.interview_score != null
          ? String(candidate.interview_percent)
          : 'NA',
      candidate_number: candidate?.candidate_number || '',
      job_role: candidate?.job_role || '',
    });
    if (!response || (!response.success && status === 'REVERT')) {
      toast.update(toastId, {
        render: 'Contact your admin for this operation',
        type: 'info',
        isLoading: false,
        autoClose: 3000,
      });
      return;
    } else if (!response || (!response.success && status != 'REVERT')) {
      toast.update(toastId, {
        render: 'Something went wrong while performing this operation',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      return;
    }
    const { success, data } = response;
    console.log({ data, success });
    if (success && status === 'REVERT') {
      toast.update(toastId, {
        render: 'Candidate Reverted to Completed Section',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      const revertedCandidate = {
        _id: data.candidate_id,
        hr_id: data.status_changed_by_id,
        contact_number: Number(data.candidate_number),
        combined_status: 'Completed',
        interview_id: data.candidate_id,
        candidate_name: data.candidate_name,
        jdposition: data.job_role,
        hrname: 'Unknown',
        scheduled_by_name: data.hr_owner,
        candidate_email: data.candidate_email,
        mcq_status: 'NA',
        coding_status: 'NA',
        interview_status: 'Complete',
        behavioral_status: 'NA',
        typing_status: 'Unknown',
        invited_date: data.date_of_interview,
        interview_percent: Number(data.interview_score) || 0,
      };
      dispatch(addCandidateToCompletedSection(revertedCandidate));
    }
  };

  const handleSelectAllCandidates = async (value) => {
    dispatch(
      updateSelectAllCandidateReducer({
        activeTab,
        activeSubSubTab: '',
        activeSubTab,
        value,
        candidates,
      }),
    );
    dispatch(toggleAllDecisionCenterCandidatesStatus({ activeSubTab, value }));
  };

  const handletoggleCandidateStatus = (candidate) => {
    console.log({ candidate });
    dispatch(toggleDecisionCenterCandidatesStatus({ activeSubTab, candidate }));
    if (candidate.isSelected) {
      dispatch(removeCandidateFromSelectedList(candidate));
    } else {
      dispatch(addcandidateToSelectedList(candidate));
    }
  };

  if (decisionCandidatesLoading) {
    return (
      <div className="w-full min-h-full">
        <Loader />
      </div>
    );
  }

  const SortIcon = ({ colKey }) => {
    const className =
      'w-4 h-4 cursor-pointer dark:hover:text-white hover:text-gray-300';
    if (sortConfig.key !== colKey)
      return <ArrowUpDown className={`${className} opacity-40`} />;
    if (sortConfig.direction === 'asc')
      return <ArrowUpAz className={className} />;
    if (sortConfig.direction === 'desc')
      return <ArrowDownZA className={className} />;
    return <ArrowUpDown className={`${className} opacity-40`} />;
  };

  return (
    <div className="relative flex flex-col w-full h-full">
      <div
        className="flex-1 mt-2"
        // style={{ maxHeight: 'calc(100vh - 200px)' }}
      >
        <style>{`
        /* Chrome, Edge, Safari */
        /* Light theme only */
        .light ::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        .light ::-webkit-scrollbar-track {
          background: transparent;
        }

        .light ::-webkit-scrollbar-thumb {
          background: #e5e7eb; /* gray-600 */
          border-radius: 100px;
        }

        .light ::-webkit-scrollbar-thumb:hover {
          background: #d1d5db; /* gray-700 */
        }

        .light ::-webkit-scrollbar-corner {
          background: transparent;
        }

        /* Firefox */
        .light * {
          scrollbar-width: thin;
          scrollbar-color: #e5e7eb  transparent;
        }
        ::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        ::-webkit-scrollbar-track {
          background: transparent;
        }

        ::-webkit-scrollbar-thumb {
          background: #4b5563; /* gray-600 */
          border-radius: 100px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #374151; /* gray-700 */
        }

        ::-webkit-scrollbar-corner {
          background: transparent;
        }

        /* Firefox */
        * {
          scrollbar-width: thin;
          scrollbar-color: #4b5563 transparent; /* thumb track */
        }
      `}</style>
        <table className="mt-1 w-full h-fit table-fixed relative ">
          <thead className="z-10 bg-bgSecondary">
            <tr className="border-b border-[#5A4479D4]">
              <th className="text-left px-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary w-12 py-4">
                {paginatedCandidates.length > 0 && (
                  <input
                    type="checkbox"
                    className="rounded bg-gray-700 border-gray-600 w-4 h-4 accent-blue-600"
                    checked={selectAllClicked}
                    onChange={(e) => {
                      handleSelectAllCandidates(e.target.checked);
                    }}
                    title="Select All"
                  />
                )}
              </th>
              {visibleColumns['Candidate Name'] && (
                <th
                  className="text-left px-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary cursor-pointer"
                  onClick={() => handleSort('candidate_name')}
                >
                  <div className="flex items-center gap-2">
                    Candidate Name <SortIcon colKey="candidate_name" />
                  </div>
                </th>
              )}
              {visibleColumns['Job Role'] && (
                <th
                  className="text-left px-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary cursor-pointer"
                  onClick={() => handleSort('job_role')}
                >
                  <div className="flex items-center gap-2">
                    Job Role <SortIcon colKey="job_role" />
                  </div>
                </th>
              )}
              {visibleColumns['Email Id'] && (
                <th className="text-left px-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary">
                  Email ID
                </th>
              )}
              {visibleColumns['Contact Number'] && (
                <th className="text-left px-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary">
                  Contact Number
                </th>
              )}
              {visibleColumns['Interview Date'] && (
                <th
                  className="text-left px-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary cursor-pointer"
                  onClick={() => handleSort('date_of_interview')}
                >
                  <div className="flex items-center gap-2">
                    Interview Date <SortIcon colKey="date_of_interview" />
                  </div>
                </th>
              )}
              {visibleColumns['Owner'] && (
                <th
                  className="text-left px-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary cursor-pointer"
                  onClick={() => handleSort('hr_owner')}
                >
                  <div className="flex items-center gap-2">
                    Owner <SortIcon colKey="hr_owner" />
                  </div>
                </th>
              )}
              {visibleColumns['Interview Score'] && (
                <th
                  className="text-left px-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary cursor-pointer"
                  onClick={() => handleSort('interview_score')}
                >
                  <div className="flex items-center gap-2">
                    Interview Score <SortIcon colKey="interview_score" />
                  </div>
                </th>
              )}
              {visibleColumns['Status'] && (
                <th className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary font-medium py-4 px-6 w-24">
                  Status
                </th>
              )}
              <th className="w-20"></th>
            </tr>
          </thead>
          {paginatedCandidates.length === 0 && (
            <tbody className="max-h-[10vh] overflow-y-auto">
              <tr>
                <td colSpan={Object.keys(visibleColumns).length + 2}>
                  <div className="flex h-60 items-center justify-center">
                    <div className="flex flex-col items-center text-center">
                      <h2 className="text-lg font-medium text-gray-400 mb-1">
                        No candidates found
                      </h2>
                      <p className="text-sm text-gray-400">
                        Try adjusting your filters or search terms.
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          )}

          {paginatedCandidates.length > 0 && (
            <tbody className="max-h-full">
              {paginatedCandidates.map((candidate, index) => (
                <tr
                  key={index}
                  className="cursor-pointer border-b dark:border-gray-700/30 border-gray-300/30 dark:hover:bg-gray-700/20 hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    onViewCandidate(candidate?.candidate_id);
                    console.log({ candidate });
                  }}
                >
                  <td
                    className="p-4"
                    onClick={() => handletoggleCandidateStatus(candidate)}
                  >
                    <input
                      type="checkbox"
                      className="rounded bg-gray-700 border-gray-600 w-4 h-4 accent-blue-600"
                      checked={candidate.isSelected || false}
                      onClick={(e) => {
                        e.stopPropagation(); // ✅ THIS is critical
                      }}
                      onChange={() => {
                        handletoggleCandidateStatus(candidate);
                      }}
                    />
                  </td>
                  {visibleColumns['Candidate Name'] && (
                    <td className="py-4 dark:text-[#CCCCCCE5] text-textSecondary font-medium px-4 text-sm w-48 truncate text-left">
                      {candidate.candidate_name || 'N/A'}
                    </td>
                  )}

                  {visibleColumns['Job Role'] && (
                    <td className="py-4 px-4 dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate text-left">
                      {candidate.job_role || 'N/A'}
                    </td>
                  )}

                  {visibleColumns['Email Id'] && (
                    <td className="py-4 px-4 dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate text-left">
                      {candidate.candidate_email || 'N/A'}
                    </td>
                  )}

                  {visibleColumns['Contact Number'] && (
                    <td className="py-4 px-4 dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate text-left">
                      {candidate.candidate_number || 'N/A'}
                    </td>
                  )}

                  {visibleColumns['Interview Date'] && (
                    <td className="py-4 px-4 dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate text-left">
                      {candidate.date_of_interview || 'N/A'}
                    </td>
                  )}

                  {visibleColumns['Owner'] && (
                    <td className="py-4 px-4 dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate text-left">
                      {candidate.hr_owner || 'N/A'}
                    </td>
                  )}

                  {visibleColumns['Interview Score'] && (
                    <td className="py-4 px-16 text-sm w-32 text-left">
                      <div
                        className={`flex items-center   justify-center border w-9 p-0.5 px-5 rounded-lg ${
                          Number(candidate.interview_score) >= 70
                            ? 'border-green-600 text-green-400'
                            : Number(candidate.interview_score) >= 30
                              ? 'border-yellow-600 text-yellow-400'
                              : 'border-red-600 text-red-400'
                        }`}
                      >
                        <span className="px-1 mx-1">
                          {candidate.interview_score
                            ? parseInt(candidate.interview_score, 10)
                            : 'NA'}
                        </span>
                      </div>
                    </td>
                  )}

                  {visibleColumns['Status'] && (
                    <td
                      className={`py-4 px-4 font-medium text-left text-sm w-24 truncate ${
                        candidate.decision === 'SELECTED'
                          ? 'text-green-500'
                          : candidate.decision === 'REJECTED'
                            ? 'text-red-500'
                            : 'text-gray-400'
                      }`}
                    >
                      {candidate.decision
                        ? candidate.decision.charAt(0) +
                          candidate.decision.slice(1).toLowerCase()
                        : 'N/A'}
                    </td>
                  )}

                  {(activeSubTab === 'REJECTED' ||
                    (activeSubTab === 'ALL' &&
                      candidate.decision === 'REJECTED')) && (
                    <td className="w-20 flex flex-row justify-center mt-4">
                      <EllipsisVertical
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Menu clicked for:', candidate);
                          setOpenMenuIndex(index);
                        }}
                      />
                      {openMenuIndex === index && (
                        <div
                          className="absolute dark:bg-gray-800/90 w-[100px]  max-h-[100px] bg-bgPrimary border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg py-2 px-2 mt-8 mr-24 z-50"
                          ref={menuRef}
                        >
                          <div className="flex flex-col space-y-3">
                            <button
                              className="w-full text-left text-sm  py-2
          dark:text-gray-300 text-gray-700
          dark:hover:bg-gray-700 hover:bg-gray-100
          dark:hover:text-white hover:text-gray-900
          transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  activeSubTab === 'REJECTED' ||
                                  activeSubTab === 'ALL'
                                ) {
                                  //    handleupdateCandidateStatus(candidate);
                                  setShowReasonModel(true);
                                  setSelectedCandidate(candidate);
                                }
                              }}
                            >
                              Revert
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          )}
          {showReasonModel && (
            <ConfirmationDialog
              open={showReasonModel}
              onClose={() => setShowReasonModel(false)}
              title="Tell Us Reason for Reverting Candidates"
              message={`Are you sure you want to Revert candidate ?`}
              confirmText="Confirm"
              cancelText="Cancel"
              takeReason={true}
              onConfirm={(reasonText) => {
                setShowReasonModel(false);
                handleupdateCandidateStatus(
                  selectedCandidate,
                  'REVERT',
                  reasonText,
                );
              }}
            />
          )}
        </table>
      </div>
      <div className="pt-4">
        <Pagination
          currentPage={currentPage}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
          label="Candidates"
        />
      </div>
    </div>
  );
}

export default DecisionCenter;
